import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { PawPrint, Printer, Syringe, ArrowLeft, ShieldCheck } from "lucide-react";
import { api, mensajeError } from "../api/client";
import { Spinner } from "../components/ui/Spinner";

interface DatosCertificado {
  certificado: {
    vacuna: string;
    lote?: string | null;
    farmaceutica?: string | null;
    viaAplicacion?: string | null;
    fechaAplicacion: string;
    confirmadoPor?: string | null;
    confirmadoPorTitulo?: string | null;
    confirmadoPorCedula?: string | null;
    fechaConfirmacion?: string | null;
  };
  mascota: {
    nombre: string;
    especie: string;
    raza?: string | null;
    clienteNombre: string;
  };
  negocio: {
    nombre: string;
    logoUrl?: string | null;
    colorPrimario?: string | null;
    direccion?: string | null;
    telefono?: string | null;
  };
}

export default function CertificadoVacuna() {
  const { token, vacunaId } = useParams();
  const [datos, setDatos] = useState<DatosCertificado | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!token || !vacunaId) return;
    api
      .get(`/portal-mascota/${token}/vacuna/${vacunaId}/certificado`)
      .then((res) => setDatos(res.data))
      .catch((err) => setError(mensajeError(err)));
  }, [token, vacunaId]);

  const colorAcento = datos?.negocio.colorPrimario || "#D97706";

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-amber-50 px-4 text-center">
        <div className="max-w-sm rounded-3xl border border-amber-100 bg-white p-8 shadow-sm">
          <PawPrint size={32} className="mx-auto mb-3 text-amber-400" />
          <p className="text-slate-600">{error}</p>
          <Link to={`/portal-mascota/${token}`} className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-amber-600 hover:underline">
            <ArrowLeft size={14} /> Volver al expediente
          </Link>
        </div>
      </div>
    );
  }

  if (!datos) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-amber-50">
        <Spinner className="h-8 w-8" />
      </div>
    );
  }

  const { certificado, mascota, negocio } = datos;

  return (
    <div className="min-h-screen bg-amber-50 px-4 py-10 print:bg-white print:py-0">
      <style>{`
        @media print {
          .no-imprimir { display: none !important; }
          body { background: white !important; }
        }
      `}</style>

      <div className="mx-auto max-w-xl">
        <div className="no-imprimir mb-4 flex items-center justify-between">
          <Link to={`/portal-mascota/${token}`} className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-slate-700">
            <ArrowLeft size={14} /> Volver al expediente
          </Link>
          <button
            onClick={() => window.print()}
            className="inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-medium text-white shadow-sm"
            style={{ backgroundColor: colorAcento }}
          >
            <Printer size={15} /> Imprimir certificado
          </button>
        </div>

        <div
          className="rounded-3xl border-4 bg-white p-8 shadow-sm sm:p-10"
          style={{ borderColor: `${colorAcento}33` }}
        >
          <div className="mb-6 flex flex-col items-center text-center">
            <div
              className="mb-3 flex h-16 w-16 items-center justify-center overflow-hidden rounded-3xl text-white"
              style={{ backgroundColor: colorAcento }}
            >
              {negocio.logoUrl ? (
                <img src={negocio.logoUrl} alt={negocio.nombre} className="h-full w-full object-cover" />
              ) : (
                <PawPrint size={28} />
              )}
            </div>
            <p className="font-display text-xl text-slate-800">{negocio.nombre}</p>
            {negocio.direccion && <p className="text-xs text-slate-400">{negocio.direccion}</p>}
          </div>

          <div className="mb-6 flex items-center justify-center gap-2 text-center">
            <ShieldCheck size={20} style={{ color: colorAcento }} />
            <h1 className="font-display text-2xl uppercase tracking-wide text-slate-800">Certificado de vacunación</h1>
          </div>

          <div className="space-y-3 rounded-2xl bg-amber-50/60 p-5 text-sm">
            <div className="flex justify-between border-b border-amber-100 pb-2">
              <span className="text-slate-400">Mascota</span>
              <span className="font-medium text-slate-800">{mascota.nombre}</span>
            </div>
            <div className="flex justify-between border-b border-amber-100 pb-2">
              <span className="text-slate-400">Especie / raza</span>
              <span className="font-medium text-slate-800">
                {mascota.especie} {mascota.raza ? `· ${mascota.raza}` : ""}
              </span>
            </div>
            <div className="flex justify-between border-b border-amber-100 pb-2">
              <span className="text-slate-400">Tutor(a)</span>
              <span className="font-medium text-slate-800">{mascota.clienteNombre}</span>
            </div>
            <div className="flex justify-between border-b border-amber-100 pb-2">
              <span className="text-slate-400">Vacuna aplicada</span>
              <span className="inline-flex items-center gap-1.5 font-medium text-slate-800">
                <Syringe size={14} /> {certificado.vacuna}
              </span>
            </div>
            {certificado.lote && (
              <div className="flex justify-between border-b border-amber-100 pb-2">
                <span className="text-slate-400">Lote</span>
                <span className="font-medium text-slate-800">{certificado.lote}</span>
              </div>
            )}
            {certificado.farmaceutica && (
              <div className="flex justify-between border-b border-amber-100 pb-2">
                <span className="text-slate-400">Laboratorio</span>
                <span className="font-medium text-slate-800">{certificado.farmaceutica}</span>
              </div>
            )}
            {certificado.viaAplicacion && (
              <div className="flex justify-between border-b border-amber-100 pb-2">
                <span className="text-slate-400">Vía de aplicación</span>
                <span className="font-medium text-slate-800">{certificado.viaAplicacion}</span>
              </div>
            )}
            <div className="flex justify-between border-b border-amber-100 pb-2">
              <span className="text-slate-400">Fecha de aplicación</span>
              <span className="font-medium text-slate-800">
                {new Date(certificado.fechaAplicacion).toLocaleDateString("es-MX", { dateStyle: "long" })}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-400">Fecha de emisión</span>
              <span className="font-medium text-slate-800">
                {new Date().toLocaleDateString("es-MX", { dateStyle: "long" })}
              </span>
            </div>
          </div>

          {certificado.confirmadoPor && (
            <div className="mt-8 flex flex-col items-center text-center">
              <div className="w-64 border-t border-slate-300 pt-2">
                <p className="font-medium text-slate-800">
                  {certificado.confirmadoPorTitulo ? `${certificado.confirmadoPorTitulo} ` : ""}
                  {certificado.confirmadoPor}
                </p>
                {certificado.confirmadoPorCedula && (
                  <p className="text-xs text-slate-400">Cédula profesional: {certificado.confirmadoPorCedula}</p>
                )}
                <p className="mt-1 text-xs text-slate-400">Firma y sello del veterinario</p>
              </div>
            </div>
          )}

          <p className="mt-6 text-center text-xs text-slate-400">
            Este certificado fue emitido y confirmado por el equipo veterinario de {negocio.nombre}.
          </p>
        </div>
      </div>
    </div>
  );
}
